import { stringify } from 'querystring';
import * as React from 'react';
import styled from 'styled-components';
import { NavigateFunction } from 'react-router';
import { toast } from 'react-toastify';
import Loading from '../../../commonComponents/Loading';
import PasswordForm, { PasswordFormState } from '../../../commonComponents/forms/PasswordForm';
import { setPassword, updatePassword } from '../../../core/api';
import { getQueryParam } from '../../../lib/utils';
import Fieldset from '../../../commonComponents/fields/Fieldset';
import FormView from '../../common/FormView';
import PasswordField from '../../../commonComponents/fields/PasswordField';
import { CheckboxInput } from '../../common/fields/Checkbox';

export default class PasswordFormContainer extends PasswordForm<PasswordProps, PasswordState> {
	constructor(props: unknown) {
		super(props);
		this.state = {
			isValid: false,
			fieldsValidation: {},
			errorMessage: null,
			loading: false,
			oldPassword: '',
			password: '',
			confirmPassword: '',
			confirmPasswordErrorMessage: '',
			workflowToken: getQueryParam('token'),
			policyChecked: false,
			termsChecked: false
		};
	}

	getPolicyLink = () => {
		const AUpolicy = 'https://vetting.com/en-au/privacy-policy';
		const UKpolicy = 'https://vetting.com/en-gb/privacy-policy';
		return window.location.hostname.includes('uk.vetting.com') ? UKpolicy : AUpolicy;
	};

	getTermsLink = () => {
		if (this.props.isUser) {
			const AUterms = 'https://vetting.com/en-au/terms-conditions';
			const UKterms = 'https://vetting.com/en-gb/terms-conditions';
			return window.location.hostname.includes('uk.vetting.com') ? UKterms : AUterms;
		}
		const AUterms = 'https://vetting.com/en-au/candidate-terms-conditions';
		const UKterms = 'https://vetting.com/en-gb/candidate-terms-conditions';
		return window.location.hostname.includes('uk.vetting.com') ? UKterms : AUterms;
	};

	handleFormSubmit = async () => {
		if (!this.state.isValid) {
			return;
		}
		await this.performAction(async () => {
			if (this.props.requireOldPassword) {
				await updatePassword(this.state.oldPassword, this.state.password);
				toast('Your password has been changed.');
				this.setState({ oldPassword: '', password: '', confirmPassword: '' });
			} else {
				await setPassword(this.props.secret, this.state.password, this.state.workflowToken);
				toast('Your password has been set. Please login.');
				const queryString: { [key: string]: string } = {};
				if (this.props.email) queryString.email = this.props.email;
				const redirectUrl = getQueryParam('redirectUrl');
				if (redirectUrl) queryString.redirectUrl = decodeURIComponent(redirectUrl);
				this.props.navigate(`/?${stringify(queryString)}`);
			}
		});
	};

	renderForm = () => {
		if (this.props.isAccountDetailsView) {
			return (
				<>
					<div className="leftColumn">
						{this.props.requireOldPassword && (
							<PasswordField
								label="Old Password"
								name="oldPassword"
								value={this.state.oldPassword}
								onValueChange={this.handleValueChange}
								validateOnMount={!this.props.requireOldPassword}
							/>
						)}
						<PasswordField
							label="New Password"
							name="password"
							value={this.state.password}
							onValueChange={this.handleValueChange}
							autoFocus={!this.props.requireOldPassword}
							validateOnMount={!this.props.requireOldPassword}
						/>
					</div>
					<div className="rightColumn">
						<PasswordField
							label="Retype Password"
							name="confirmPassword"
							value={this.state.confirmPassword}
							onValueChange={this.handleValueChange}
							errorMessage={this.state.confirmPasswordErrorMessage}
							validateOnMount={!this.props.requireOldPassword}
						/>
					</div>
					{!this.props.requireOldPassword && (
						<>
							<CheckboxWrapper>
								<CheckboxInput
									value={this.state.policyChecked}
									onValueChange={(_fieldName, value: boolean) => {
										this.setState({ policyChecked: value });
									}}
									name="policy"
								/>
								<label className="k-checkbox-label" htmlFor="policy">
									I have read and agreed to the{' '}
									<a href={this.getPolicyLink()} target="_blank" rel="noreferrer">
										Privacy Policy
									</a>
								</label>
							</CheckboxWrapper>
							<CheckboxWrapper>
								<CheckboxInput
									value={this.state.termsChecked}
									onValueChange={(_fieldName, value: boolean) => {
										this.setState({ termsChecked: value });
									}}
									name="terms"
								/>
								<label className="k-checkbox-label" htmlFor="terms">
									I have read and agreed to the{' '}
									<a href={this.getTermsLink()} target="_blank" rel="noreferrer">
										Terms & Conditions
									</a>
								</label>
							</CheckboxWrapper>
						</>
					)}
				</>
			);
		}
		return (
			<>
				{this.props.requireOldPassword && (
					<PasswordField
						label="Old Password"
						name="oldPassword"
						value={this.state.oldPassword}
						onValueChange={this.handleValueChange}
						validateOnMount={!this.props.requireOldPassword}
					/>
				)}
				<PasswordField
					label="New Password"
					name="password"
					value={this.state.password}
					onValueChange={this.handleValueChange}
					autoFocus={!this.props.requireOldPassword}
					validateOnMount={!this.props.requireOldPassword}
				/>
				<PasswordField
					label="Retype Password"
					name="confirmPassword"
					value={this.state.confirmPassword}
					onValueChange={this.handleValueChange}
					errorMessage={this.state.confirmPasswordErrorMessage}
					validateOnMount={!this.props.requireOldPassword}
				/>
				{!this.props.requireOldPassword && (
					<>
						<CheckboxWrapper>
							<CheckboxInput
								value={this.state.policyChecked}
								onValueChange={(_fieldName, value: boolean) => {
									this.setState({ policyChecked: value });
								}}
								name="policy"
							/>
							<label className="k-checkbox-label" htmlFor="policy">
								I have read and agreed to the{' '}
								<a href={this.getPolicyLink()} target="_blank" rel="noreferrer">
									Privacy Policy
								</a>
							</label>
						</CheckboxWrapper>
						<CheckboxWrapper>
							<CheckboxInput
								value={this.state.termsChecked}
								onValueChange={(_fieldName, value: boolean) => {
									this.setState({ termsChecked: value });
								}}
								name="terms"
							/>
							<label className="k-checkbox-label" htmlFor="terms">
								I have read and agreed to the{' '}
								<a href={this.getTermsLink()} target="_blank" rel="noreferrer">
									Terms & Conditions
								</a>
							</label>
						</CheckboxWrapper>
					</>
				)}
			</>
		);
	};

	determineDisabled(): boolean {
		if (this.props.requireOldPassword) {
			return false;
		}
		if (this.props.isUser) {
			return !(this.state.policyChecked && this.state.termsChecked);
		}
		return !this.state.policyChecked;
	}

	render() {
		return (
			<>
				<FormView
					className="resetPassword"
					onFormSubmit={this.handleFormSubmit}
					submitButtonText={this.props.submitButtonText ?? this.props.title}
					errorMessage={this.state.errorMessage}
					disabled={this.determineDisabled()}
				>
					<Fieldset title={this.props.title} centred>
						{this.renderForm()}
					</Fieldset>
				</FormView>
				{this.state.loading && <Loading />}
			</>
		);
	}
}

interface PasswordProps {
	requireOldPassword?: boolean;
	title: string;
	email?: string;
	secret?: string;
	navigate: NavigateFunction;
	isAccountDetailsView?: boolean;
	submitButtonText?: string;
	isUser?: boolean;
}

interface PasswordState extends PasswordFormState {
	oldPassword: string;
	workflowToken: string;
	policyChecked: boolean;
	termsChecked: boolean;
}

const CheckboxWrapper = styled.div`
	display: block;
	margin: 20px auto;
	width: 350px;
	text-align: left;

	label {
		display: inline-flex;
		margin: 0;
		width: auto;
		height: auto;
	}
`;
